import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2, Star } from "lucide-react";
import axios from "axios";
import { useSiteConfig } from "@/context/SiteConfigContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { formatted_phone, google_review_url } = useSiteConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "👋 Hi! I'm here to help with your junk removal questions. How can I assist you today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: `I apologize, but I'm having trouble right now. Please call us at ${formatted_phone} for immediate assistance!` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        data-testid="chat-widget-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 md:bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isOpen ? "bg-gray-700 rotate-90" : "bg-[#b51a00] hover:bg-[#8a1300]"
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chat-window"
          className="fixed bottom-36 md:bottom-24 right-6 z-50 w-[350px] max-w-[calc(100vw-3rem)] bg-white rounded-lg shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#1a1a1a] text-white p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#b51a00] rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Overhaul Ops Support
                </h4>
                <p className="text-xs text-gray-400">Typically replies instantly</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[300px] overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-lg ${
                    msg.role === "user"
                      ? "bg-[#b51a00] text-white rounded-br-none"
                      : "bg-white text-gray-800 shadow-sm rounded-bl-none"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-[#b51a00]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Google Review Link */}
          {google_review_url && (
            <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200">
              <a 
                href={google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-link"
                className="flex items-center gap-2 text-sm text-yellow-800 hover:text-yellow-900"
              >
                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                <span>⭐ Leave us a Google Review!</span>
              </a>
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 rounded-full"
                disabled={loading}
              />
              <Button 
                type="submit" 
                disabled={loading || !input.trim()}
                className="rounded-full w-10 h-10 p-0 bg-[#b51a00] hover:bg-[#8a1300]"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
